/**
=========================================================
* Utility Functions for Public Registration
=========================================================
*/

// Generate unique cookie user ID
export const generateCookieUserId = () => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substr(2, 9);
  return `user_${timestamp}_${randomString}`;
};

// Local storage keys
const STORAGE_KEYS = {
  REGISTRATION_DATA: 'cricket_registration_data',
  LAST_REGISTRATION: 'cricket_last_registration'
};

// Save form data to local storage
export const saveToLocalStorage = (formData) => {
  try {
    const dataToStore = {
      ...formData,
      timestamp: Date.now(),
      version: '1.0'
    };
    
    localStorage.setItem(STORAGE_KEYS.REGISTRATION_DATA, JSON.stringify(dataToStore));
    localStorage.setItem(STORAGE_KEYS.LAST_REGISTRATION, Date.now().toString());
    
    return true;
  } catch (error) {
    console.error('Error saving to local storage:', error);
    return false;
  }
};

// Load form data from local storage
export const loadFromLocalStorage = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.REGISTRATION_DATA);
    if (!storedData) return null;
    
    const parsedData = JSON.parse(storedData);
    
    // Check if data is not too old (30 days)
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    if (parsedData.timestamp < thirtyDaysAgo) {
      clearLocalStorage();
      return null;
    }
    
    // Remove metadata before returning
    const { timestamp, version, ...formData } = parsedData;
    return formData;
  } catch (error) {
    console.error('Error loading from local storage:', error);
    return null;
  }
};

// Clear local storage
export const clearLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEYS.REGISTRATION_DATA);
    localStorage.removeItem(STORAGE_KEYS.LAST_REGISTRATION);
    return true;
  } catch (error) {
    console.error('Error clearing local storage:', error);
    return false;
  }
};

// Check if stored data exists
export const hasStoredData = () => {
  try {
    const storedData = localStorage.getItem(STORAGE_KEYS.REGISTRATION_DATA);
    if (!storedData) return false;
    
    const parsedData = JSON.parse(storedData);
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    
    return parsedData.timestamp > thirtyDaysAgo;
  } catch (error) {
    console.error('Error checking stored data:', error);
    return false;
  }
};

// Get last registration timestamp
export const getLastRegistrationTime = () => {
  try {
    const timestamp = localStorage.getItem(STORAGE_KEYS.LAST_REGISTRATION);
    return timestamp ? parseInt(timestamp) : null;
  } catch (error) {
    console.error('Error getting last registration time:', error);
    return null;
  }
};

// Format mobile number
export const formatMobileNumber = (mobile) => {
  if (!mobile) return '';
  return mobile.replace(/\D/g, '').slice(0, 10);
};

// Validate mobile number
export const validateMobileNumber = (mobile) => {
  const cleaned = formatMobileNumber(mobile);
  return /^\d{10}$/.test(cleaned);
};

// Validate email (if needed in future)
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Format date for display
export const formatDate = (dateString) => {
  if (!dateString) return 'TBD';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    return dateString;
  }
};

// Get file size in human readable format
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Validate file type
export const validateFileType = (file, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png']) => {
  return allowedTypes.includes(file.type);
};

// Validate file size (default 5MB)
export const validateFileSize = (file, maxSizeInMB = 5) => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};
